import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../../features/auth/authSlice';
import {
  useGetUserFavouritePropertyQuery,
  useGetUserSavedPropertiesQuery,
  useDeleteFavoriteMutation,
} from '../../features/api/apiSlice';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PropertyCard from '../home/components/PropertyCard';
import EmptyState from './components/EmptyState';
import SortingControls from './components/SortingControls';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import { useToast } from '../../components/ui/Toast';
import UserLayout from '../user-dashboard/UserLayout';

const SavedPropertiesPage = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const addToast = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('savedDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Primary: production favourites API. Fallback: legacy saved properties table.
  const {
    data: favourites = [],
    isLoading: favLoading,
    isError: favError,
    refetch: refetchFavourites,
  } = useGetUserFavouritePropertyQuery(undefined, { skip: !isAuthenticated });

  const {
    data: legacySaved = [],
    isLoading: legacyLoading,
    refetch: refetchLegacy,
  } = useGetUserSavedPropertiesQuery(undefined, { skip: !isAuthenticated });

  const isLoading = favLoading || legacyLoading;
  const isError = favError && !(Array.isArray(legacySaved) && legacySaved.length);
  const refetch = () => {
    refetchFavourites();
    refetchLegacy();
  };

  const [deleteFavorite] = useDeleteFavoriteMutation();

  const apiSavedProperties = useMemo(() => {
    if (Array.isArray(favourites) && favourites.length) {
      return favourites.map((p) => ({ ...p, id: p.id, isFavorite: true }));
    }
    if (Array.isArray(legacySaved) && legacySaved.length) {
      return legacySaved.map((item) => ({
        ...(item.property || item),
        savedDate: item.saved_date,
        id: item.property_id || item.property?.id || item.id,
        isFavorite: true,
      }));
    }
    return [];
  }, [favourites, legacySaved]);

  const getFilteredProperties = () => {
    let filtered = [...apiSavedProperties];

    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(property =>
        property?.title?.toLowerCase()?.includes(query) ||
        property?.location?.toLowerCase()?.includes(query) ||
        property?.city?.toLowerCase()?.includes(query) ||
        property?.address?.toLowerCase()?.includes(query) ||
        property?.type?.toLowerCase()?.includes(query)
      );
    }

    // Sort properties (client-side for now)
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (sortBy === 'savedDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  };

  const filteredProperties = getFilteredProperties();

  // Handle property selection for bulk actions
  const handlePropertySelect = (propertyId) => {
    setSelectedProperties(prev => {
      const isSelected = prev?.includes(propertyId);
      const newSelection = isSelected 
        ? prev?.filter(id => id !== propertyId)
        : [...prev, propertyId];
      
      setShowBulkActions(newSelection?.length > 0);
      return newSelection;
    });
  };

  // Handle removing a single property from favorites
  const handleRemoveProperty = async (propertyId) => {
    if (window.confirm('Are you sure you want to remove this property from your saved list?')) {
      try {
        await deleteFavorite(propertyId).unwrap();
        addToast('Property removed from saved list.', 'info'); // Show info toast
        setSelectedProperties(prev => prev?.filter(id => id !== propertyId));
        refetch(); // Refetch saved properties to update the list
      } catch (err) {
        console.error('Failed to remove favorite property:', err);
        addToast('Failed to remove property. Please try again.', 'error'); // Use toast for error
      }
    }
  };

  // Handle bulk remove
  const handleBulkRemove = async () => {
    if (window.confirm(`Remove ${selectedProperties?.length} properties from saved list?`)) {
      try {
        await Promise.all(selectedProperties.map(id => deleteFavorite(id).unwrap()));
        addToast(`${selectedProperties.length} properties removed from saved list.`, 'info'); // Show info toast
        setSelectedProperties([]);
        setShowBulkActions(false);
        refetch(); // Refetch saved properties to update the list
      } catch (err) {
        console.error('Failed to bulk remove favorite properties:', err);
        addToast('Failed to bulk remove properties. Please try again.', 'error'); // Use toast for error
      }
    }
  };

  const handleSortChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  if (!isAuthenticated) {
    return (
      <UserLayout title="Saved Properties" subtitle="Sign in required">
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center max-w-lg mx-auto">
          <Icon name="Lock" size={36} className="mx-auto text-slate-300 mb-3" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Sign in required</h2>
          <p className="text-slate-500 mb-6">Please log in to view your saved properties.</p>
          <Button onClick={() => navigate('/login')}>Go to Login</Button>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout
      title="Saved Properties"
      subtitle="Your shortlisted homes and land opportunities"
    >
      <div className="max-w-7xl space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search saved properties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              iconName="Search"
              iconPosition="left"
              onClick={() => navigate('/property-search-browse')}
            >
              Find more
            </Button>
            {selectedProperties?.length > 0 && (
              <Button
                variant="ghost"
                iconName="Square"
                iconPosition="left"
                onClick={() => setShowBulkActions(!showBulkActions)}
              >
                Select mode
              </Button>
            )}
          </div>
        </div>

        <SortingControls
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          totalCount={filteredProperties?.length}
        />

        {isError ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
            <p className="text-rose-800 font-medium mb-2">Could not load saved properties</p>
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm animate-pulse"
              >
                <div className="h-48 bg-slate-100" />
                <div className="p-4 space-y-3">
                  <div className="h-6 bg-slate-100 rounded w-3/4" />
                  <div className="h-4 bg-slate-100 rounded w-full" />
                  <div className="h-4 bg-slate-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredProperties?.length > 0 ? (
          <div
            className={`grid gap-6 ${
              viewMode === 'grid'
                ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                : 'grid-cols-1'
            }`}
          >
            {filteredProperties?.map((property) => (
              <PropertyCard
                key={property?.id}
                property={property}
                isFavorite={true}
                isSelected={selectedProperties?.includes(property?.id)}
                onSelect={handlePropertySelect}
                onRemove={handleRemoveProperty}
                showCheckbox={showBulkActions}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            type={'all'}
            folderName={''}
            onCreateFolder={() => navigate('/property-search-browse')}
          />
        )}
      </div>

      {showBulkActions && (
        <BulkActionsToolbar
          selectedCount={selectedProperties?.length}
          onRemove={handleBulkRemove}
          onClearSelection={() => {
            setSelectedProperties([]);
            setShowBulkActions(false);
          }}
        />
      )}
    </UserLayout>
  );
};

export default SavedPropertiesPage;
